import React, { useState, useEffect } from 'react';
import { formatDate, getStatusBadge, getStatusBadgeVariant, getStatusIcon, statusColors, statusLabels } from '../utils/utils';
import { Badge } from 'react-bootstrap';

const ApplicationTimelinePopup = ({application}) => {
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const token = process.env.REACT_APP_API_TOKEN;
  const LMS = process.env.REACT_APP_API_LMS;

  useEffect(() => {
    if (application?.id) {
      fetchApplicationTimeline(application.id);
    }
  }, [application?.id]);

  const fetchApplicationTimeline = async (applicationId) => {
    try {
      setLoading(true);
      const response = await fetch(`${LMS}/api/get-application-thread?id=${applicationId}`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.data?.success && data.data?.result) {
        const transformedTimeline = data.data.result.map(item => ({
          status: item.status,
          date: item.created_at,
          time: new Date(item.created_at).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          description: getStatusDescription(item.status, item.updated_by),
          updatedBy: item.updated_by,
          role: item.role,
          comment: item.comment
        })).reverse(); 
        setTimelineData(transformedTimeline);
      }
    } catch (error) {
      console.error('Error fetching application timeline:', error);
      if (application?.statusHistory) {
        setTimelineData(application.statusHistory);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusDescription = (status, updatedBy) => {
    const updaterName = updatedBy ? `${updatedBy.first_name} ${updatedBy.last_name}` : 'System';
    
    const descriptions = {
      'applied': `Application submitted successfully`,
      'accept': `Application accepted by ${updaterName}`,
      'hired': `Congratulations! You have been hired by ${updaterName}`,
      'rejected': `Application was declined by ${updaterName}`,
      'interview': `Interview scheduled by ${updaterName}`,
      'pending': `Application is under review`
    };

    return descriptions[status] || `Status updated to ${status} by ${updaterName}`;
  };

  if (!application) return null;

  return (
    <div className="application-timeline-popup">
        <div className="d-flex align-items-center gap-2 mb-4">
            {/* {getStatusBadge(application.status)} */}
            <Badge className='text-capitalize' bg={getStatusBadgeVariant(application.status)}>
              {application.status?.replace("_",' ')}
            </Badge>
            <span className="text-muted small">
                Applied on {formatDate(application.appliedDate)}
            </span>
        </div>
      <div className="position-relative mb-4">
        {loading ? (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading timeline...</span>
            </div>
          </div>
        ) : timelineData?.length === 0 ? (
          <div className="text-center py-4">
            <div className="d-flex align-items-center justify-content-center mb-2">
              <div className="application-timeline-badge bg-primary me-3">
                {getStatusIcon('pending')}
              </div>
              <div>
                <h6 className="mb-1 fw-semibold text-muted text-start">Your Application is Under Review</h6>
                <p className="text-muted mb-0 small text-start">
                  We're currently reviewing your application. You'll be notified of any updates.
                </p>
              </div>
            </div>
          </div>
        ) : (
          timelineData?.map((item, index) => (
            <div key={index} className="d-flex mb-3">
              <div className="me-3">
                <div
                  className={`application-timeline-badge bg-${
                    statusColors[item.status] || 'secondary'
                  }`}
                >
                  {getStatusIcon(item.status)}
                </div>
              </div>
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between">
                  <div>
                    <h6 className="mb-1 fw-semibold">
                      {statusLabels[item.status] || item.status}
                    </h6>
                    <p className="text-muted mb-0 small">{item.description}</p>
                    {item.comment && (
                      <p className="text-muted mb-0 small fst-italic">
                        Comment: {item.comment}
                      </p>
                    )}
                  </div>
                  <div className="text-end small text-muted">
                    <div>{formatDate(item.date)}</div>
                    <div>{item.time}</div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <style>
        {`
        .application-timeline-badge {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 34px;
            height: 34px;
            border-radius: 50%;
            color: #fff;
        }
        `}
      </style>
    </div>
  );
};

export default ApplicationTimelinePopup;